"use strict";
// Deleting your Flix account.
//
// The hard part of this screen is not the buttons, it is telling the
// truth. Deleting a Flix account ends your access and your account data;
// it does NOT erase your purchases from the store's records, because
// those are the shop's commercial history and Flix does not own them.
// Saying "everything will be erased" would be easier to write and would
// be a lie, so this screen says what actually happens on both sides.
//
// Three deliberate steps, because this cannot be undone: read what will
// happen, prove it is you with a code, then confirm. The proof and the
// confirmation are separate presses on separate screens — a single
// "are you sure?" is not a real gate on something irreversible.
(function () {
    function t(hubEl, key, fallback) {
        return hubEl.dataset[key] || fallback;
    }
    var esc = function (value) {
        return window.FlixHubFormat.escapeHtml(value);
    };
    var WARNING_ICON = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.2 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.2a2 2 0 0 0-3.4 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
    /** Where the flow currently is. Held here, never in the URL: a deletion half-armed is not a place to link somebody back to. */
    var challengeId = null;
    var maskedEmail = null;
    function sectionLabelHtml(label) {
        return ('<div style="font-family:var(--mono);font-size:10px;letter-spacing:2px;color:var(--accent);margin:0 0 10px;text-transform:uppercase">' +
            esc(label) +
            "</div>");
    }
    function backHtml(hubEl) {
        return ('<button type="button" data-flix-hub-nav="settings" style="display:inline-flex;align-items:center;gap:7px;' +
            "background:none;border:none;padding:0;margin:0 0 18px;color:var(--dim);font-family:var(--body);" +
            'font-size:13px;cursor:pointer" style-hover="color:var(--text)">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="transform:scaleX(1)"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>' +
            esc(t(hubEl, "i18nBackToSettings", "Back to settings")) +
            "</button>");
    }
    function errorBoxHtml() {
        return ('<div data-flix-delete-error hidden style="margin:14px 0 0;padding:11px 14px;border-radius:12px;' +
            'background:rgba(224,86,74,.1);border:1px solid rgba(224,86,74,.4);color:var(--error);font-size:13px"></div>');
    }
    function showError(contentEl, message) {
        var box = contentEl.querySelector("[data-flix-delete-error]");
        if (!box)
            return;
        box.textContent = message;
        box.hidden = false;
    }
    function bulletHtml(text) {
        return ('<li style="margin:0 0 7px;line-height:1.65">' + esc(text) + "</li>");
    }
    /**
     * Step one: what actually happens, on both sides of the line.
     *
     * The kept list is not a footnote. Somebody deleting an account usually
     * wants to know their receipts still exist — and if they are deleting
     * BECAUSE they want the store to forget them, they need to know this
     * screen is not the thing that does that.
     */
    function renderExplain(hubEl, contentEl) {
        contentEl.innerHTML =
            backHtml(hubEl) +
                sectionLabelHtml(t(hubEl, "i18nDeleteLabel", "Delete account")) +
                '<div style="border-radius:var(--radius-md);background:var(--surface);border:1px solid rgba(224,86,74,.3);padding:20px">' +
                '<div style="display:flex;align-items:flex-start;gap:13px">' +
                '<span aria-hidden="true" style="flex:none;width:40px;height:40px;border-radius:12px;background:rgba(224,86,74,.12);' +
                'border:1px solid rgba(224,86,74,.35);color:var(--error);display:flex;align-items:center;justify-content:center">' +
                WARNING_ICON +
                "</span>" +
                '<div style="flex:1;min-width:0">' +
                '<h2 style="font-family:var(--display);font-weight:800;font-size:17px;margin:0;color:var(--text)">' +
                esc(t(hubEl, "i18nDeleteHeading", "Delete your Flix account")) +
                "</h2>" +
                '<p style="font-size:13px;color:var(--dim);margin:8px 0 0;line-height:1.65">' +
                esc(t(hubEl, "i18nDeleteIntro", "This cannot be undone. Signing in again later creates a brand-new account.")) +
                "</p>" +
                "</div>" +
                "</div>" +
                '<div style="margin-top:20px">' +
                '<div style="font-size:13px;font-weight:700;color:var(--text);margin-bottom:8px">' +
                esc(t(hubEl, "i18nDeleteRemovedTitle", "What is deleted")) +
                "</div>" +
                '<ul style="margin:0;padding-inline-start:20px;font-size:13px;color:var(--dim)">' +
                bulletHtml(t(hubEl, "i18nDeleteRemovedAccount", "Your Flix account and profile details")) +
                bulletHtml(t(hubEl, "i18nDeleteRemovedMethods", "Every way of signing in to it")) +
                bulletHtml(t(hubEl, "i18nDeleteRemovedSessions", "All your sessions and devices, everywhere")) +
                bulletHtml(t(hubEl, "i18nDeleteRemovedAccess", "Your access to orders and files through this account")) +
                "</ul>" +
                "</div>" +
                '<div style="margin-top:18px">' +
                '<div style="font-size:13px;font-weight:700;color:var(--text);margin-bottom:8px">' +
                esc(t(hubEl, "i18nDeleteKeptTitle", "What is kept")) +
                "</div>" +
                '<ul style="margin:0;padding-inline-start:20px;font-size:13px;color:var(--dim)">' +
                bulletHtml(t(hubEl, "i18nDeleteKeptPurchases", "Your purchases stay in the store's records. Orders, receipts and invoices are not deleted.")) +
                bulletHtml(t(hubEl, "i18nDeleteKeptEmail", "Email preferences are handled separately. If you have unsubscribed, that stays in effect.")) +
                "</ul>" +
                "</div>" +
                errorBoxHtml() +
                '<button type="button" data-flix-delete-action="start" style="width:100%;margin-top:22px;min-height:48px;' +
                "border-radius:var(--radius-button);border:1px solid var(--error);background:transparent;color:var(--error);" +
                'font-family:var(--display);font-weight:800;font-size:15px;cursor:pointer;transition:background .2s" ' +
                'style-hover="background:rgba(224,86,74,.1)">' +
                esc(t(hubEl, "i18nDeleteContinue", "Continue")) +
                "</button>" +
                "</div>";
        window.flixCompileHoverStyles(contentEl);
    }
    /** Step two: prove it is you. A code, to an address this account already proved it controls. */
    function renderConfirm(hubEl, contentEl) {
        var _a;
        var sentTo = t(hubEl, "i18nDeleteCodeSentTo", "We sent a confirmation code to");
        contentEl.innerHTML =
            backHtml(hubEl) +
                sectionLabelHtml(t(hubEl, "i18nDeleteLabel", "Delete account")) +
                '<div style="border-radius:var(--radius-md);background:var(--surface);border:1px solid rgba(224,86,74,.3);padding:20px">' +
                '<h2 style="font-family:var(--display);font-weight:800;font-size:17px;margin:0;color:var(--text)">' +
                esc(t(hubEl, "i18nDeleteConfirmHeading", "Confirm it is you")) +
                "</h2>" +
                '<p style="font-size:13px;color:var(--dim);margin:8px 0 0;line-height:1.6">' +
                esc(sentTo) +
                " " +
                '<span style="color:var(--text);font-weight:700">' +
                esc(maskedEmail !== null && maskedEmail !== void 0 ? maskedEmail : "") +
                "</span>" +
                "</p>" +
                '<label style="display:block;font-size:12px;color:var(--dim);margin:18px 0 7px" for="flix-delete-code">' +
                esc(t(hubEl, "i18nDeleteCodeLabel", "Confirmation code")) +
                "</label>" +
                '<input id="flix-delete-code" data-flix-delete-code type="text" inputmode="numeric" autocomplete="one-time-code" ' +
                'maxlength="6" style="width:100%;min-height:48px;padding:0 15px;border-radius:var(--radius-button);' +
                "border:1px solid var(--border);background:var(--surface2);color:var(--text);font-family:var(--mono);" +
                'font-size:18px;letter-spacing:6px;text-align:center">' +
                errorBoxHtml() +
                '<button type="button" data-flix-delete-action="confirm" style="width:100%;margin-top:18px;min-height:48px;' +
                "border-radius:var(--radius-button);border:none;background:var(--error);color:#fff;" +
                'font-family:var(--display);font-weight:800;font-size:15px;cursor:pointer">' +
                esc(t(hubEl, "i18nDeleteFinal", "Permanently delete my account")) +
                "</button>" +
                '<p style="font-size:12px;color:var(--faint);margin:12px 0 0;line-height:1.6">' +
                esc(t(hubEl, "i18nDeleteFinalNote", "You will be signed out on every device. Your purchases stay in the store's records.")) +
                "</p>" +
                "</div>";
        window.flixCompileHoverStyles(contentEl);
        (_a = contentEl.querySelector("[data-flix-delete-code]")) === null || _a === void 0 ? void 0 : _a.focus();
    }
    /**
     * Back from the provider, proved.
     *
     * The same final press as the email route, minus the code box — asking
     * for one now would be asking twice for the same proof. The warning
     * stays, because this is still the button that ends the account.
     */
    function renderProviderConfirmed(hubEl, contentEl) {
        contentEl.innerHTML =
            backHtml(hubEl) +
                sectionLabelHtml(t(hubEl, "i18nDeleteLabel", "Delete account")) +
                '<div style="border-radius:var(--radius-md);background:var(--surface);border:1px solid rgba(224,86,74,.3);padding:20px">' +
                '<h2 style="font-family:var(--display);font-weight:800;font-size:17px;margin:0;color:var(--text)">' +
                esc(t(hubEl, "i18nDeleteProviderConfirmedHeading", "Identity confirmed")) +
                "</h2>" +
                '<p style="font-size:13px;color:var(--dim);margin:8px 0 0;line-height:1.65">' +
                esc(t(hubEl, "i18nDeleteProviderConfirmedBody", "You signed in again to confirm it is you. This is the last step — it cannot be undone.")) +
                "</p>" +
                errorBoxHtml() +
                '<button type="button" data-flix-delete-action="confirm" style="width:100%;margin-top:18px;min-height:48px;' +
                "border-radius:var(--radius-button);border:none;background:var(--error);color:#fff;" +
                'font-family:var(--display);font-weight:800;font-size:15px;cursor:pointer">' +
                esc(t(hubEl, "i18nDeleteFinal", "Permanently delete my account")) +
                "</button>" +
                '<p style="font-size:12px;color:var(--faint);margin:12px 0 0;line-height:1.6">' +
                esc(t(hubEl, "i18nDeleteFinalNote", "You will be signed out on every device. Your purchases stay in the store's records.")) +
                "</p>" +
                "</div>";
        window.flixCompileHoverStyles(contentEl);
    }
    /** The end. No account to go back to, so this is not a panel like the others. */
    function renderDone(hubEl, contentEl) {
        contentEl.innerHTML = window.FlixHubRender.emptyState({
            title: t(hubEl, "i18nDeleteDoneTitle", "Your account has been deleted"),
            message: t(hubEl, "i18nDeleteDoneMessage", "You have been signed out everywhere. Your purchases remain in the store's records."),
            action: { label: t(hubEl, "i18nDeleteDoneCta", "Back to the store"), href: "/" },
        });
        window.flixCompileHoverStyles(contentEl);
    }
    function messageFor(hubEl, err) {
        var status = typeof err === "object" && err !== null && "status" in err
            ? err.status
            : 0;
        var code = typeof err === "object" && err !== null && "code" in err
            ? err.code
            : "";
        // 409 is not "you got it wrong" — it is "this account has no way to
        // confirm", which needs an entirely different sentence and an action
        // the person can actually take.
        if (status === 409 || code === "IDENTITY.STEP_UP_UNAVAILABLE") {
            return t(hubEl, "i18nDeleteNoEmail", "To delete your account we need to confirm it is you by email. Add and verify an email address first, in Settings.");
        }
        if (status === 403) {
            return t(hubEl, "i18nDeleteWrongCode", "That code is not right, or it has expired.");
        }
        return t(hubEl, "i18nDeleteFailed", "Something went wrong. Please try again.");
    }
    /**
     * Coming back from the provider.
     *
     * The identity service appends `stepup=ok` or `stepup=failed` and sends
     * the browser here. The marker says only which way it went — the proof
     * itself lives on the session, server-side, so nothing in the URL can
     * arm anything. It is stripped immediately either way, so a reload or a
     * shared link does not replay the result.
     */
    function readStepUpReturn() {
        var params = new URLSearchParams(window.location.search);
        var marker = params.get("stepup");
        if (marker !== "ok" && marker !== "failed")
            return null;
        params.delete("stepup");
        var url = new URL(window.location.href);
        url.search = params.toString();
        window.history.replaceState(window.history.state, "", url.toString());
        return marker;
    }
    function render(hubEl, contentEl) {
        challengeId = null;
        maskedEmail = null;
        var returned = readStepUpReturn();
        if (returned === "ok") {
            // Already proved, at the provider. Straight to the final press —
            // asking for a code now would be asking twice for the same thing.
            renderProviderConfirmed(hubEl, contentEl);
            return;
        }
        renderExplain(hubEl, contentEl);
        if (returned === "failed") {
            showError(contentEl, t(hubEl, "i18nDeleteProviderFailed", "We could not confirm it was you. Please try again."));
        }
    }
    /**
     * Asks for whichever proof this account can give.
     *
     * An account with a verified address gets a code. One that signs in
     * only with Google or Discord has no address to send to, so it
     * re-authorizes with that provider instead — a full round trip, not a
     * reuse of the session it already holds.
     */
    async function start(hubEl, contentEl) {
        var _a, _b;
        var button = contentEl.querySelector('[data-flix-delete-action="start"]');
        if (button) {
            button.disabled = true;
            button.style.opacity = "0.6";
        }
        var started;
        try {
            started = await window.FlixAuthClient.startAccountDeletion();
        }
        catch (err) {
            if (button) {
                button.disabled = false;
                button.style.opacity = "";
            }
            showError(contentEl, messageFor(hubEl, err));
            return;
        }
        if (started.method === "provider") {
            // Leaves the storefront. The identity service owns the round trip
            // and brings the browser back here with the outcome.
            var identityBase = (_b = (_a = window.flixIdentityConfig) === null || _a === void 0 ? void 0 : _a.apiBaseUrl) !== null && _b !== void 0 ? _b : "";
            if (identityBase === "") {
                showError(contentEl, messageFor(hubEl, {}));
                return;
            }
            window.location.href =
                identityBase.replace(/\/+$/, "") +
                    "/me/deletion/step-up/oauth/" +
                    encodeURIComponent(started.provider) +
                    "/start";
            return;
        }
        challengeId = started.challengeId;
        maskedEmail = started.maskedEmail;
        renderConfirm(hubEl, contentEl);
    }
    /**
     * Spends the proof, then deletes.
     *
     * Two calls for the email route, in that order: the code has to be
     * recorded on the session before the deletion will accept it, and if it
     * is wrong nothing has happened yet. The provider route has already
     * spent its proof at the callback, so there is only the second call —
     * and no code to collect, which is why an absent `challengeId` is a
     * legitimate state here rather than an error.
     */
    async function confirm(hubEl, contentEl) {
        var _a;
        var input = contentEl.querySelector("[data-flix-delete-code]");
        var button = contentEl.querySelector('[data-flix-delete-action="confirm"]');
        var code = ((_a = input === null || input === void 0 ? void 0 : input.value) !== null && _a !== void 0 ? _a : "").trim();
        // Only the email route has an input to read. Its absence means the
        // provider already proved this session at the callback.
        var needsCode = input !== null;
        if (needsCode && (code.length === 0 || challengeId === null)) {
            showError(contentEl, t(hubEl, "i18nDeleteWrongCode", "That code is not right, or it has expired."));
            return;
        }
        if (button) {
            button.disabled = true;
            button.style.opacity = "0.6";
        }
        try {
            if (needsCode && challengeId !== null) {
                await window.FlixAuthClient.verifyAccountDeletion(challengeId, code);
            }
            await window.FlixAuthClient.deleteAccount();
        }
        catch (err) {
            if (button) {
                button.disabled = false;
                button.style.opacity = "";
            }
            showError(contentEl, messageFor(hubEl, err));
            return;
        }
        // The session is gone server-side and its cookies are cleared, so the
        // Hub must stop presenting a signed-in account. Telling the rest of
        // the page first means the header icon flips with this screen, not a
        // page load later.
        document.dispatchEvent(new CustomEvent("flix:auth:state-changed", { detail: { state: "signed-out" } }));
        renderDone(hubEl, contentEl);
    }
    window.FlixHubDelete = { render: render, start: start, confirm: confirm };
})();
